import type { EMediaType } from "@frontend-shared/types";
import { createContext } from "react";

// NOTE: The concrete callAssistant error keys a caller may hand to startDownload.
// Narrowing this (instead of a bare string) lets the provider call t(errorKey)
// without a type assertion.
export type TMediaDownloadErrorKey =
  | "details.play_bar.errors.failed_to_download"
  | "details.play_bar.errors.failed_to_download_video"
  | "details.play_bar.errors.failed_to_download_bundle";

export interface IMediaDownloadState {
  downloadingType: EMediaType;
  downloadProgress: number;
  label: string;
}

export interface IMediaDownloadArchiveTextFile {
  name: string;
  content: string;
}

// NOTE: NSAI-3847 — present when the download is a "Download all": the fetched
// recording is written into a .zip alongside these already-rendered text files
// instead of being saved on its own. The archive is produced while the
// recording streams in, so it costs no extra copy of the media in memory, and
// progress / cancel keep working exactly as they do for a plain download.
export interface IMediaDownloadArchive {
  mediaEntryName: string;
  textFiles: IMediaDownloadArchiveTextFile[];
  modifiedAt?: Date;
}

export interface IStartMediaDownloadProps {
  meetingUuid: string;
  url: string;
  // NOTE: The download filename (meeting-title based, UUID fallback) — see
  // buildMediaDownloadFilename (NSAI-4143).
  filename: string;
  mediaType: EMediaType;
  errorKey: TMediaDownloadErrorKey;
  label: string;
  archive?: IMediaDownloadArchive;
}

export interface IActiveMediaDownload extends IMediaDownloadState {
  meetingUuid: string;
}

export interface IMediaDownloadContext {
  // NOTE: Keyed by meetingUuid so an in-flight download's progress survives the
  // RecordingPlayBar (and MeetingDetails route) unmounting on navigation and is
  // restored when the user returns to the meeting page (NSAI-3838).
  downloads: Record<string, IMediaDownloadState>;
  startDownload: (props: IStartMediaDownloadProps) => void;
  cancelDownload: (meetingUuid: string) => void;
  // NOTE: Aborts and drops every in-flight download — used to purge state on
  // logout / account switch so it can't leak across authenticated sessions.
  clearAll: () => void;
}

export const MediaDownloadContext = createContext<IMediaDownloadContext | null>(
  null,
);
